import React, {useRef} from "react";
import {useTranslation} from "react-i18next";
import IconHome from '../assets/icons/home.svg?react';
import IconBook from '../assets/icons/book.svg?react';
import IconBuilding from '../assets/icons/building.svg?react';
import IconShare from '../assets/icons/share.svg?react';
import IconEye from '../assets/icons/eye.svg?react';
import IconPicture from '../assets/icons/picture.svg?react';
import IconBriefcase from '../assets/icons/briefcase.svg?react';
import IconPending from '../assets/icons/pending.svg?react';
import IconRoute from '../assets/icons/route.svg?react';
import brandMarkRed from '../assets/images/brand-mark-red.png';
import {ProfileDialog} from "./dialogs/ProfileDialog.tsx";
import {Partner} from "../domain/entities/Partner.ts";
import {initialsOf} from "../utils/initials.ts";

type SidebarNavItemProps = {
    icon: React.ReactNode;
    label: string;
    active: boolean;
    collapsed: boolean;
    onClick: () => void;
};

const SidebarNavItem: React.FC<SidebarNavItemProps> = ({icon, label, active, collapsed, onClick}) => (
    <button
        type="button"
        title={collapsed ? label : undefined}
        onClick={onClick}
        className={`flex items-center gap-2.5 px-2.5 py-[9px] rounded-[10px] w-full max-[860px]:w-auto text-left transition-colors duration-200 max-[860px]:flex-none max-[860px]:whitespace-nowrap ${
            collapsed ? 'justify-center' : ''
        } max-[860px]:justify-center ${
            active
                ? 'bg-neutralBlack-100 text-neutralBlack-900'
                : 'text-neutralBlack-600 hover:bg-neutralBlack-100 hover:text-neutralBlack-900'
        }`}
    >
        <span className="w-[18px] h-[18px] flex-shrink-0 flex items-center justify-center">
            {icon}
        </span>
        {!collapsed && <span className="text-sm font-medium max-[560px]:hidden">{label}</span>}
    </button>
);

type DashboardSidebarProps = {
    partnerData: Partner | null;
    currentUserDisplayName?: string;
    currentUserEmail?: string;
    isAdmin: boolean;
    activeSection: string;
    collapsed: boolean;
    onToggleCollapse: () => void;
    setPathView: (path: string) => void;
};

export const DashboardSidebar: React.FC<DashboardSidebarProps> = ({
    partnerData, currentUserDisplayName, currentUserEmail, isAdmin, activeSection, collapsed, onToggleCollapse, setPathView
}) => {
    const {t} = useTranslation('dashboard')
    const profileDialog = useRef<React.ElementRef<typeof ProfileDialog>>(null)
    const currentUserName = currentUserDisplayName || currentUserEmail || partnerData?.name || ''

    return (
        <aside className={`${collapsed ? 'w-[64px]' : 'w-[232px]'} max-[860px]:w-full shrink-0 bg-neutralBlack-50 border-r border-neutralBlack-200 flex flex-col h-full transition-all duration-200 overflow-hidden max-[860px]:flex-row max-[860px]:items-center max-[860px]:gap-2.5 max-[860px]:h-auto max-[860px]:border-r-0 max-[860px]:border-b max-[860px]:border-neutralBlack-200 max-[860px]:px-3.5 max-[860px]:py-2 max-[860px]:sticky max-[860px]:top-0 max-[860px]:z-40 max-[860px]:bg-white max-[860px]:overflow-x-auto max-[860px]:no-scrollbar`}>

            {/* Brand */}
            <div className="p-3 max-[860px]:p-0 max-[860px]:flex-none">
                <div className={`flex items-center justify-center w-full max-[860px]:w-auto bg-white max-[860px]:bg-transparent border border-neutralBlack-200 max-[860px]:border-0 rounded-xl shadow-sm max-[860px]:shadow-none ${collapsed ? 'px-2 py-2' : 'px-3.5 py-3'} max-[860px]:px-0 max-[860px]:py-0`}>
                    {partnerData?.corporateLogo ? (
                        <img
                            src={partnerData.corporateLogo}
                            alt={partnerData.name}
                            className={`${collapsed ? 'w-6 h-6 object-contain' : 'max-h-8 max-w-full object-contain'} max-[860px]:w-auto max-[860px]:max-h-7`}
                        />
                    ) : collapsed ? (
                        <span className="w-6 h-6 flex-none rounded-lg bg-[#FBEDEA] flex items-center justify-center text-[10px] font-bold text-[#DA6254]">
                            51
                        </span>
                    ) : (
                        <img src={brandMarkRed} alt="51TRIPS" className="w-[88px] h-auto object-contain max-[860px]:w-[72px]"/>
                    )}
                </div>
            </div>

            {/* Nav */}
            <nav className="flex flex-col gap-0.5 px-2 mt-1 max-[860px]:flex-row max-[860px]:flex-1 max-[860px]:min-w-0 max-[860px]:mt-0 max-[860px]:px-0 max-[860px]:gap-1 max-[860px]:overflow-x-auto max-[860px]:no-scrollbar">
                {(isAdmin || partnerData?.role === 'agency') && (
                    <SidebarNavItem
                        icon={<IconHome className="w-full h-full"/>}
                        label={t('overview', {defaultValue: 'Vista general'})}
                        active={activeSection === 'overview'}
                        collapsed={collapsed}
                        onClick={() => setPathView('overview')}
                    />
                )}
                <SidebarNavItem
                    icon={<IconBook className="w-full h-full"/>}
                    label={partnerData?.role === 'agency' ? t('sidebar.packsAndTripbooks') : t('trips')}
                    active={activeSection === 'tripbooks'}
                    collapsed={collapsed}
                    onClick={() => setPathView('tripbooks')}
                />
                {isAdmin && (
                    <SidebarNavItem
                        icon={<IconBuilding className="w-full h-full"/>}
                        label={t('agencies')}
                        active={activeSection === 'agencies'}
                        collapsed={collapsed}
                        onClick={() => setPathView('agencies')}
                    />
                )}
                {isAdmin && (
                    <SidebarNavItem
                        icon={<IconEye className="w-full h-full"/>}
                        label={t('sidebar.codesMonitor')}
                        active={activeSection === 'codes-monitor'}
                        collapsed={collapsed}
                        onClick={() => setPathView('codes-monitor')}
                    />
                )}
                {isAdmin && (
                    <SidebarNavItem
                        icon={<IconRoute className="w-full h-full"/>}
                        label={t('sidebar.pipelines')}
                        active={activeSection === 'pipelines'}
                        collapsed={collapsed}
                        onClick={() => setPathView('pipelines')}
                    />
                )}
                {partnerData?.role === 'agency' && (
                    <SidebarNavItem
                        icon={<IconPending className="w-full h-full"/>}
                        label={t('activation.sidebar')}
                        active={activeSection === 'not-activated'}
                        collapsed={collapsed}
                        onClick={() => setPathView('not-activated')}
                    />
                )}
                {partnerData?.role === 'agency' && (
                    <SidebarNavItem
                        icon={<IconShare className="w-full h-full"/>}
                        label={t('sidebar.referrals')}
                        active={activeSection === 'referrals'}
                        collapsed={collapsed}
                        onClick={() => setPathView('referrals')}
                    />
                )}
                {isAdmin && (
                    <SidebarNavItem
                        icon={<IconPicture className="w-full h-full"/>}
                        label={t('sidebar.marketingMockups')}
                        active={activeSection === 'marketing-mockups'}
                        collapsed={collapsed}
                        onClick={() => setPathView('marketing-mockups')}
                    />
                )}
                {isAdmin && (
                    <SidebarNavItem
                        icon={<IconBriefcase className="w-full h-full"/>}
                        label={t('sidebar.agencyKits')}
                        active={activeSection === 'agency-kits'}
                        collapsed={collapsed}
                        onClick={() => setPathView('agency-kits')}
                    />
                )}
            </nav>

            <div className="flex-1 max-[860px]:hidden"/>

            {/* Collapse toggle — no equivalent in the horizontal mobile bar */}
            <div className="px-2 pb-1 max-[860px]:hidden">
                <button
                    type="button"
                    title={collapsed ? t('expandMenu', {defaultValue: 'Expand'}) : t('collapseMenu', {defaultValue: 'Collapse'})}
                    onClick={onToggleCollapse}
                    className="flex items-center justify-center w-full py-2 rounded-xl text-neutralBlack-400 hover:bg-neutralBlack-100 hover:text-neutralBlack-600 transition-colors duration-200"
                >
                    <svg
                        className={`w-4 h-4 transition-transform duration-200 ${collapsed ? 'rotate-180' : ''}`}
                        viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M10 12L6 8l4-4"/>
                    </svg>
                    {!collapsed && <span className="text-xs ml-2 whitespace-nowrap">{t('collapseMenu', {defaultValue: 'Collapse'})}</span>}
                </button>
            </div>

            {/* Profile */}
            <button
                type="button"
                onClick={() => profileDialog.current?.open()}
                className={`flex items-center gap-2.5 border-t border-neutralBlack-200 py-3 hover:bg-neutralBlack-100 transition-colors duration-200 ${collapsed ? 'justify-center px-2' : 'px-3'} max-[860px]:flex-none max-[860px]:border-t-0 max-[860px]:border-l max-[860px]:border-neutralBlack-200 max-[860px]:py-1.5 max-[860px]:px-2 max-[860px]:ml-1`}
            >
                <span className="w-[26px] h-[26px] flex-none rounded-lg bg-neutralBlack-100 flex items-center justify-center overflow-hidden">
                    <span className="text-[10.5px] font-semibold text-neutralBlack-700">
                        {initialsOf(currentUserName)}
                    </span>
                </span>
                {!collapsed && (
                    <span className="min-w-0 flex flex-col items-start max-[560px]:hidden">
                        <span className="text-[12.5px] font-medium text-neutralBlack-900 whitespace-nowrap overflow-hidden text-ellipsis max-w-full">
                            {currentUserName}
                        </span>

                    </span>
                )}
            </button>
            <ProfileDialog ref={profileDialog}/>
        </aside>
    )
}
